import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

export default function Login() {
  const [email, setEmail]               = useState('');
  const [password, setPassword]         = useState('');
  const [loading, setLoading]           = useState(false);
  const [twoFARequired, setTwoFARequired] = useState(false);
  const [twoFAToken, setTwoFAToken]     = useState('');
  const [pendingCreds, setPendingCreds] = useState(null);
  const [branding, setBranding]         = useState({ logoData: null, portalName: 'Srauto Vizija' });
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetch('/api/settings/branding')
      .then(r => r.json())
      .then(d => setBranding(d))
      .catch(() => {});
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const creds = twoFARequired ? pendingCreds : { email, password };
      const user = await login(creds.email, creds.password, twoFARequired ? twoFAToken : undefined);
      toast.success(`Sveiki, ${user.name}!`);
      navigate(user.role === 'admin' ? '/admin' : '/dashboard');
    } catch (err) {
      if (err.response?.status === 206 && err.response?.data?.twoFactorRequired) {
        setPendingCreds({ email, password });
        setTwoFARequired(true);
        setLoading(false);
        return;
      }
      toast.error(err.response?.data?.error || 'Prisijungimo klaida');
    } finally {
      setLoading(false);
    }
  };

  const features = [
    { icon: '📹', text: 'Realaus laiko kamerų stebėjimas' },
    { icon: '📊', text: 'Automatinės ataskaitos Excel ir PDF' },
    { icon: '🚶', text: 'Žmonių, automobilių ir transporto skaičiavimas' },
    { icon: '🗺️', text: 'Kamerų žemėlapis su koordinatėmis' },
    { icon: '🔔', text: 'Automatiniai įspėjimai viršijus slenkstį' },
    { icon: '📈', text: 'Periodo palyginimas ir tendencijų analizė' },
  ];

  return (
    <div className="login-page" style={{ display:'flex', minHeight:'100vh' }}>
      <div className="login-bg" />

      {/* Kairė pusė – marketingas */}
      <div style={{
        flex: 1, display:'flex', flexDirection:'column', justifyContent:'center',
        padding:'60px', maxWidth:580,
        background:'linear-gradient(135deg, rgba(26,39,68,0.97) 0%, rgba(15,25,50,0.99) 100%)',
        borderRight:'1px solid rgba(255,255,255,0.07)',
        position:'relative', zIndex:1
      }} className="login-marketing">
        <div style={{ marginBottom:40 }}>
          <div style={{ display:'flex', alignItems:'center', gap:12, marginBottom:24 }}>
            {branding.logoData
              ? <img src={branding.logoData} alt="logo" style={{ width:44, height:44, borderRadius:10, objectFit:'cover' }} />
              : <div className="logo-icon">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M15 10l-4 4l6 6l4-16l-18 7l4 2l2 6l3-4"/>
                  </svg>
                </div>
            }
            <span style={{ fontSize:20, fontWeight:800, color:'var(--text)' }}>{branding.portalName || 'Srauto Vizija'}</span>
          </div>
          <h2 style={{ fontSize:32, fontWeight:800, color:'#fff', lineHeight:1.2, marginBottom:16 }}>
            Stebėkite srautą.<br />
            <span style={{ color:'var(--accent)' }}>Optimizuokite verslą.</span>
          </h2>
          <p style={{ fontSize:15, color:'rgba(255,255,255,0.6)', lineHeight:1.7 }}>
            Profesionalus transporto ir žmonių srauto monitoringo įrankis, 
            skirtas kiekvienam verslui – nuo parduotuvių iki logistikos centrų.
          </p>
        </div>

        <div style={{ display:'flex', flexDirection:'column', gap:14, marginBottom:48 }}>
          {features.map((f, i) => (
            <div key={i} style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{
                width:36, height:36, borderRadius:10, fontSize:16,
                background:'rgba(59,130,246,0.15)', border:'1px solid rgba(59,130,246,0.2)',
                display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0
              }}>{f.icon}</div>
              <span style={{ fontSize:14, color:'rgba(255,255,255,0.75)' }}>{f.text}</span>
            </div>
          ))}
        </div>

        {/* Kontaktai */}
        <div style={{
          padding:'20px 24px', borderRadius:12,
          background:'rgba(59,130,246,0.08)', border:'1px solid rgba(59,130,246,0.2)'
        }}>
          <p style={{ fontSize:13, fontWeight:700, color:'var(--accent)', marginBottom:12 }}>
            📞 Norite išbandyti? Susisiekite su mumis:
          </p>
          <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
            <a href="tel:+37068754432" style={{ fontSize:13, color:'rgba(255,255,255,0.7)', textDecoration:'none', display:'flex', alignItems:'center', gap:8 }}>
              📱 <span>+370 687 54432</span>
            </a>
            <a href="mailto:info@samperna.lt?subject=Srautovizija%20demo&body=Sveiki%2C%20noriu%20su%C5%BEinoti%20daugiau%20apie%20portalo%20galimybes%20ir%20gauti%20demo%20versij%C4%85.%20Susisiekite." style={{ fontSize:13, color:'rgba(255,255,255,0.7)', textDecoration:'none', display:'flex', alignItems:'center', gap:8 }}>
              ✉️ <span>info@samperna.lt</span>
            </a>
            <a href="https://samperna.lt" target="_blank" rel="noreferrer" style={{ fontSize:13, color:'rgba(255,255,255,0.7)', textDecoration:'none', display:'flex', alignItems:'center', gap:8 }}>
              🌐 <span>samperna.lt</span>
            </a>
          </div>
          <p style={{ fontSize:11, color:'rgba(255,255,255,0.35)', marginTop:12 }}>
            UAB Samperna · Apsaugos ir stebėjimo sistemos
          </p>
        </div>
      </div>

      {/* Dešinė pusė – prisijungimas */}
      <div style={{ flex:1, display:'flex', alignItems:'center', justifyContent:'center', padding:40 }}>
        <div className="login-card" style={{ width:'100%', maxWidth:420 }}>
          <div className="login-logo">
            <h1>Prisijungimas</h1>
            <p>Įveskite savo paskyros duomenis</p>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label className="form-label">El. paštas</label>
              <input className="form-input" type="email" value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="jusu@el.pastas.lt" required autoFocus />
            </div>
            <div className="form-group">
              <label className="form-label">Slaptažodis</label>
              <input className="form-input" type="password" value={password}
                onChange={e => setPassword(e.target.value)}
                placeholder="••••••••" required />
            </div>
            {twoFARequired && (
              <div className="form-group">
                <label className="form-label">🔐 2FA kodas iš programėlės</label>
                <input className="form-input" value={twoFAToken}
                  onChange={e => setTwoFAToken(e.target.value.replace(/\D/g,'').slice(0,6))}
                  placeholder="000000" maxLength={6} autoFocus
                  style={{ fontSize:24, textAlign:'center', letterSpacing:8 }} />
              </div>
            )}
            <button className="btn btn-primary" type="submit" disabled={loading}
              style={{ width:'100%', justifyContent:'center', marginTop:8 }}>
              {loading ? <span className="loading-spinner" /> : 'Prisijungti'}
            </button>
          </form>

          <div style={{ textAlign:'center', marginTop:16 }}>
            <Link to="/forgot-password" style={{ color:'var(--text-muted)', fontSize:13, textDecoration:'none' }}>
              Pamiršote slaptažodį?
            </Link>
          </div>

          <div style={{ marginTop:32, paddingTop:24, borderTop:'1px solid var(--border)', textAlign:'center' }}>
            <p style={{ fontSize:12, color:'var(--text-muted)', marginBottom:8 }}>
              Neturite paskyros?
            </p>
            <a href="mailto:info@samperna.lt?subject=Srautovizija%20demo&body=Sveiki%2C%20noriu%20su%C5%BEinoti%20daugiau%20apie%20portalo%20galimybes%20ir%20gauti%20demo%20versij%C4%85.%20Susisiekite." style={{
              fontSize:13, color:'var(--accent)', textDecoration:'none', fontWeight:600
            }}>
              Susisiekite ir gaukite demo prieigą →
            </a>
          </div>
        </div>
      </div>

      {/* Mobiliai – slėpti kairę pusę */}
      <style>{`
        @media (max-width: 768px) {
          .login-marketing { display: none !important; }
        }
      `}</style>
    </div>
  );
}